import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Trash2, Plus, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatErr, downloadDoc, fmtDay } from "@/lib/api";
import { DatePicker } from "@/components/DatePicker";

const BLANK = { name: "", issuer: "", number: "", issued: "", expiry: "", document_id: "none" };
const expired = (d) => d && new Date(d) < new Date();

const AddLicense = ({ candidateId, docs, onSaved }) => {
  const [f, setF] = useState(BLANK);
  const save = async () => {
    try {
      const { data } = await api.post(`/candidates/${candidateId}/licenses`, { ...f, document_id: f.document_id === "none" ? null : f.document_id });
      onSaved(data);
      setF(BLANK);
      toast.success("License added");
    } catch (e) { toast.error(formatErr(e)); }
  };
  const inp = (k, ph, type = "text") => <Input data-testid={`license-${k}-input`} type={type} placeholder={ph} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />;
  return (
    <div className="mt-5 grid gap-2 rounded-md border border-dashed border-stone-300 p-4 sm:grid-cols-2" data-testid="add-license-form">
      {inp("name", "License / certification name")}
      {inp("issuer", "Issuing body")}
      {inp("number", "License number")}
      <Select value={f.document_id} onValueChange={(v) => setF({ ...f, document_id: v })}>
        <SelectTrigger data-testid="license-doc-select"><SelectValue placeholder="Attach document" /></SelectTrigger>
        <SelectContent><SelectItem value="none">No document</SelectItem>{docs.map((d) => <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>)}</SelectContent>
      </Select>
      <label className="text-xs text-stone-500">Issued<DatePicker testId="license-issued-input" value={f.issued} onChange={(v) => setF({ ...f, issued: v })} /></label>
      <label className="text-xs text-stone-500">Expires<DatePicker testId="license-expiry-input" value={f.expiry} onChange={(v) => setF({ ...f, expiry: v })} /></label>
      <Button data-testid="license-save-button" disabled={!f.name} onClick={save} className="sm:col-span-2"><Plus className="mr-1.5 h-4 w-4" />Add license</Button>
    </div>
  );
};

export const LicensesPanel = ({ candidate, mode, onUpdate }) => {
  const [docs, setDocs] = useState([]);
  const canEdit = mode === "admin" || mode === "self";
  useEffect(() => {
    if (!canEdit) return;
    api.get(mode === "self" ? "/documents/mine" : `/documents/owner/candidate/${candidate.id}`).then((r) => setDocs(r.data));
  }, [candidate.id, mode, canEdit]);

  const verify = async (l, v) => {
    try { onUpdate((await api.patch(`/admin/candidates/${candidate.id}/licenses/${l.id}`, { verified: v })).data); } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (l) => {
    try { onUpdate((await api.delete(`/candidates/${candidate.id}/licenses/${l.id}`)).data); } catch (e) { toast.error(formatErr(e)); }
  };
  const lics = candidate.licenses || [];

  return (
    <div data-testid="licenses-panel">
      {!lics.length && <p className="text-sm text-stone-500">No licenses or certifications listed yet.</p>}
      <div className="space-y-3">
        {lics.map((l) => (
          <div key={l.id} data-testid={`license-${l.id}`} className="flex flex-wrap items-center gap-3 rounded-md border border-stone-200 p-4">
            {l.verified ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-600" />}
            <div className="min-w-0 flex-1">
              <p className="font-medium text-stone-900">{l.name}</p>
              <p className="text-xs text-stone-500">{[l.issuer, l.number && `#${l.number}`, l.issued && `Issued ${fmtDay(l.issued)}`, l.expiry && `Expires ${fmtDay(l.expiry)}`].filter(Boolean).join(" · ")}</p>
              <div className="mt-1.5 flex gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${l.verified ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid={`license-verified-${l.id}`}>{l.verified ? "Verified by VirtuForce" : "Pending verification"}</span>
                {expired(l.expiry) && <span className="rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-mono uppercase text-red-700">Expired</span>}
              </div>
            </div>
            {l.document_id && <Button size="sm" variant="ghost" data-testid={`license-doc-${l.id}`} onClick={() => downloadDoc({ id: l.document_id, filename: `${l.name}` })}><Paperclip className="mr-1 h-3.5 w-3.5" />Document</Button>}
            {mode === "admin" && <label className="flex items-center gap-2 text-xs text-stone-500">Verified<Switch data-testid={`license-verify-${l.id}`} checked={!!l.verified} onCheckedChange={(v) => verify(l, v)} /></label>}
            {canEdit && <Button size="icon" variant="ghost" data-testid={`license-delete-${l.id}`} onClick={() => remove(l)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>}
          </div>
        ))}
      </div>
      {canEdit && <AddLicense candidateId={candidate.id} docs={docs} onSaved={onUpdate} />}
    </div>
  );
};
